import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from '@react-google-maps/api';
import axios from 'axios';
import { useSocketTracking } from '../hooks/useSocketTracking';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Phone,
  ArrowLeft,
  Car,
  MapPin,
  Clock,
  Navigation,
  CheckCircle,
  CheckCheck,
  AlertCircle,
  XCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { confirmBoarding, cancelRequest, raiseSos } from '../services/carpoolTripService';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629
};

// Status badge colors
const statusColors = {
  started: 'bg-blue-500',
  arrived: 'bg-green-500',
  picked_up: 'bg-purple-500',
  completed: 'bg-gray-500'
};

const statusLabels = {
  started: 'On the way',
  arrived: 'Driver Arrived',
  picked_up: 'In transit',
  completed: 'Completed'
};

export default function LiveTrackingPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [directions, setDirections] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  
  // Get user from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);
  
  // Initialize Socket.IO tracking
  const {
    isConnected,
    driverLocation,
    rideStatus,
    eta,
    driverInfo,
    error: socketError
  } = useSocketTracking(requestId, 'rider', user?.id);
  
  // Fetch initial tracking info from API
  const fetchTrackingInfo = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/carpool/requests/${requestId}/tracking`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrackingInfo(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching tracking info:', err);
      setError(err.response?.data?.detail || 'Failed to load tracking info');
      setLoading(false);
    }
  }, [requestId]);
  
  useEffect(() => {
    if (requestId) {
      fetchTrackingInfo();
    }
  }, [requestId, fetchTrackingInfo]);
  
  // Calculate directions when driver location updates
  useEffect(() => {
    if (!driverLocation || !trackingInfo?.rider?.location || !window.google) return;
    
    const directionsService = new window.google.maps.DirectionsService();
    
    directionsService.route(
      {
        origin: {
          lat: driverLocation.latitude,
          lng: driverLocation.longitude
        },
        destination: getPickupLocation(),
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === 'OK') {
          setDirections(result);
        }
      }
    );
  }, [driverLocation, trackingInfo]);
  
  // Get pickup location (rider's location)
  const getPickupLocation = () => {
    if (!trackingInfo?.rider?.location) return defaultCenter;
    const loc = trackingInfo.rider.location;
    // Handle both GeoJSON format and simple lat/lng
    if (loc.coordinates) {
      return { lat: loc.coordinates[1], lng: loc.coordinates[0] };
    }
    return { lat: loc.latitude, lng: loc.longitude };
  };
  
  // Get current driver location for marker
  const getDriverMarkerPosition = () => {
    if (driverLocation) {
      return { lat: driverLocation.latitude, lng: driverLocation.longitude };
    }
    if (trackingInfo?.driver?.location) {
      const loc = trackingInfo.driver.location;
      if (loc.coordinates) {
        return { lat: loc.coordinates[1], lng: loc.coordinates[0] };
      }
      if (loc.latitude && loc.longitude) {
        return { lat: loc.latitude, lng: loc.longitude };
      }
    }
    return null;
  };
  
  // Current status (prefer Socket.IO, fallback to API)
  const currentStatus = rideStatus || trackingInfo?.status || 'started';
  const currentDriverInfo = driverInfo || trackingInfo?.driver;
  
  // Handle call driver
  const handleCallDriver = () => {
    if (currentDriverInfo?.phone) {
      window.location.href = `tel:${currentDriverInfo.phone}`;
    }
  };

  const canConfirmBoarding = currentStatus === 'arrived' || currentStatus === 'picked_up';
  const canCancel = currentStatus === 'started' || currentStatus === 'arrived';

  const handleConfirmBoarding = async () => {
    try {
      await confirmBoarding(requestId);
      toast.success('Your driver has been notified that you boarded.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not confirm boarding');
    }
  };

  const handleSos = async () => {
    if (!window.confirm('Send SOS? This alerts your driver and emergency contacts with your location.')) {
      return;
    }
    try {
      await raiseSos({
        requestId,
        latitude: driverLocation?.latitude,
        longitude: driverLocation?.longitude,
      });
      toast.success('Your driver and emergency contacts have been alerted.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send SOS');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this ride? The driver will be notified and the route re-optimized.')) {
      return;
    }
    try {
      await cancelRequest(requestId);
      toast.success('Your seat has been released.');
      navigate('/carpool');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel the ride');
    }
  };
  
  // Map load callback
  const onMapLoad = useCallback((map) => {
    setMapRef(map);
    setMapsLoaded(true);
  }, []);
  
  // Fit bounds when we have both markers
  useEffect(() => {
    if (mapRef && driverLocation && trackingInfo?.rider?.location) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(getDriverMarkerPosition());
      bounds.extend(getPickupLocation());
      mapRef.fitBounds(bounds, { padding: 50 });
    }
  }, [mapRef, driverLocation, trackingInfo]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading tracking information...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/carpool')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Carpool
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/carpool')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold">Live Tracking</h1>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm text-gray-500">
                  {isConnected ? 'Connected' : 'Connecting...'}
                </span>
              </div>
            </div>
          </div>
          <Badge className={statusColors[currentStatus]}>
            {statusLabels[currentStatus]}
          </Badge>
        </div>
      </div>
      
      {/* Map Container */}
      <div className="flex-1 relative">
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={getDriverMarkerPosition() || getPickupLocation()}
            zoom={15}
            onLoad={onMapLoad}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false
            }}
          >
            {/* Driver Marker */}
            {mapsLoaded && getDriverMarkerPosition() && (
              <Marker
                position={getDriverMarkerPosition()}
                icon={{
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="white" stroke-width="3"/>
                      <path d="M20 12 L26 24 L20 22 L14 24 Z" fill="white"/>
                    </svg>
                  `),
                  anchor: new window.google.maps.Point(20, 20),
                  scaledSize: new window.google.maps.Size(40, 40),
                  rotation: driverLocation?.heading || 0
                }}
                title="Driver"
              />
            )}
            
            {/* Pickup Location Marker */}
            {mapsLoaded && (
              <Marker
                position={getPickupLocation()}
                icon={{
                  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="40" height="50" viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 0 C31 0 40 9 40 20 C40 35 20 50 20 50 C20 50 0 35 0 20 C0 9 9 0 20 0" fill="#22C55E"/>
                      <circle cx="20" cy="18" r="8" fill="white"/>
                    </svg>
                  `),
                  anchor: new window.google.maps.Point(20, 50),
                  scaledSize: new window.google.maps.Size(40, 50)
                }}
                title="Pickup Location"
              />
            )}
            
            {/* Route */}
            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: '#3B82F6',
                    strokeWeight: 5,
                    strokeOpacity: 0.8
                  }
                }}
              />
            )}
          </GoogleMap>
        </LoadScript>
        
        {/* ETA Overlay */}
        {(eta || directions) && currentStatus === 'started' && (
          <div className="absolute top-4 left-4 right-4">
            <Card className="bg-white/95 backdrop-blur">
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-semibold">
                        {eta?.minutes || (directions?.routes[0]?.legs[0]?.duration?.text) || '...'} away
                      </p>
                      <p className="text-sm text-gray-500">
                        {eta?.distance ? `${(eta.distance / 1000).toFixed(1)} km` : 
                         directions?.routes[0]?.legs[0]?.distance?.text || 'Calculating...'}
                      </p>
                    </div>
                  </div>
                  <Navigation className="h-6 w-6 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Arrived Notification */}
        {currentStatus === 'arrived' && (
          <div className="absolute top-4 left-4 right-4">
            <Card className="bg-green-500 text-white">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-8 w-8" />
                  <div>
                    <p className="font-bold text-lg">Driver has arrived!</p>
                    <p className="text-green-100">Please head to the pickup point</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      
      {/* Bottom Card - Driver Info */}
      <div className="bg-white shadow-lg rounded-t-3xl">
        <div className="p-4">
          {/* Driver Info */}
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-gray-200 rounded-full flex items-center justify-center">
              <Car className="h-7 w-7 text-gray-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{currentDriverInfo?.name || 'Driver'}</h3>
              {trackingInfo?.vehicle && (
                <p className="text-gray-600">
                  {trackingInfo.vehicle.color} {trackingInfo.vehicle.make} {trackingInfo.vehicle.model}
                </p>
              )}
              {trackingInfo?.vehicle?.license_plate && (
                <Badge variant="outline" className="mt-1">
                  {trackingInfo.vehicle.license_plate}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              className="flex-1" 
              variant="outline"
              onClick={handleCallDriver}
              disabled={!currentDriverInfo?.phone}
            >
              <Phone className="h-4 w-4 mr-2" />
              Call Driver
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                const pos = getDriverMarkerPosition();
                if (pos) {
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${pos.lat},${pos.lng}`,
                    '_blank'
                  );
                }
              }}
            >
              <MapPin className="h-4 w-4 mr-2" />
              Navigate
            </Button>
          </div>

          {/* Carpool actions: attendance, safety, cancellation */}
          <div className="flex gap-3 mt-3">
            {canConfirmBoarding && (
              <Button
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                onClick={handleConfirmBoarding}
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                I've Boarded
              </Button>
            )}
            <Button
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
              onClick={handleSos}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              SOS
            </Button>
            {canCancel && (
              <Button
                variant="outline"
                className="flex-1 text-gray-600"
                onClick={handleCancel}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Socket Error Toast */}
      {socketError && (
        <div className="fixed bottom-24 left-4 right-4 bg-red-500 text-white p-3 rounded-lg shadow-lg">
          <p className="text-sm">{socketError}</p>
        </div>
      )}
    </div>
  );
}
